import { useCallback, useEffect, useRef, useState } from 'react';
import { Tooltip } from './Tooltip';
import { useLanguage } from '../lib/language';
import { hexToColourName } from '../lib/colour-name';
import { resolveAssetUrl, type ChatResponseItem } from '../api/client';

/**
 * 2×2 grid of the 4 variations returned by `n_variations=4`.
 *
 * v7   — base grid: thumbnail + seed + "Use this one" / "Select" button.
 * v7.1 — keyboard nav (arrow keys move between tiles; Enter picks).
 * v7.3 — polish:
 *        - Click any tile to BLOW IT UP inline (~70% area, siblings shrink
 *          to dimmed thumbnails in the corners). Click again, click the
 *          blown-up image background, or press Escape to collapse.
 *        - Hover a dimmed sibling → it brightens so the user can A/B
 *          compare before picking.
 *        - Hover metadata (seed + iris colour + mood + lighting) appears
 *          above each tile using the v7.1 portal-fixed Tooltip.
 *        - When blown up, arrow keys cycle siblings and Enter picks the
 *          focused sibling.
 */
export function VariationsGrid(props: {
  items: ChatResponseItem[];
  onPick: (item: ChatResponseItem) => void;
  onDismiss: () => void;
  announce?: (message: string) => void;
}) {
  const { items, onPick, onDismiss, announce } = props;
  const { lang } = useLanguage();
  const keepRef = useRef<HTMLButtonElement | null>(null);
  const tileRefs = useRef<(HTMLButtonElement | null)[]>([]);

  // v7 — highlighted tile (preview cursor).
  const [highlightedIdx, setHighlightedIdx] = useState<number>(0);
  // v7.3 — blown-up tile (-1 = none).
  const [blownUpIdx, setBlownUpIdx] = useState<number>(-1);
  // v7.3 — tap-and-compare: a sibling is "peeking" when hovered.
  const [peekIdx, setPeekIdx] = useState<number>(-1);

  // Announce + focus on mount of the variations state.
  useEffect(() => {
    window.setTimeout(() => {
      keepRef.current?.focus({ preventScroll: true });
    }, 0);
    announce?.('Four variations are ready — pick one');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // v7.3 — Escape closes the blow-up.
  useEffect(() => {
    if (blownUpIdx < 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setBlownUpIdx(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [blownUpIdx]);

  const moveHighlight = useCallback(
    (delta: number) => {
      setHighlightedIdx((prev) => {
        const next = (prev + delta + items.length) % items.length;
        window.setTimeout(
          () => tileRefs.current[next]?.focus({ preventScroll: true }),
          0,
        );
        return next;
      });
    },
    [items.length],
  );

  const onKeyDownGrid = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        moveHighlight(1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        moveHighlight(-1);
      } else if (e.key === 'Enter' || e.key === ' ') {
        const item = items[highlightedIdx];
        if (item) {
          e.preventDefault();
          onPick(item);
        }
      }
    },
    [highlightedIdx, items, moveHighlight, onPick],
  );

  // v7.3 — tap-to-blow-up: clicking the blown-up image collapses,
  // clicking a sibling blows IT up instead.
  const handleTileClick = (idx: number, item: ChatResponseItem) => {
    if (blownUpIdx === idx) {
      // Already blown up — collapse on second click.
      setBlownUpIdx(-1);
    } else {
      setBlownUpIdx(idx);
    }
    onPick(item);
  };

  return (
    <div
      role="region"
      aria-label="Four variations — pick one"
      onKeyDown={onKeyDownGrid}
      className="flex flex-col gap-3 rounded-2xl border border-neutral-200 bg-white p-4 dark:border-neutral-800 dark:bg-neutral-900"
    >
      <header className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
          {lang === 'plain' ? 'Pick a variation' : 'Variation grid'}
        </h2>
        <button
          ref={keepRef}
          type="button"
          onClick={onDismiss}
          className="inline-flex h-11 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-3 text-xs font-medium text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
        >
          {lang === 'plain' ? 'Keep previous' : 'Keep current'}
        </button>
      </header>

      {/* v7.3 — main grid. When blownUpIdx >= 0, the chosen tile gets
          ~70% width and the siblings shrink to ~80px thumbnails in
          the corners (dimmed, peek-highlighted on hover). */}
      <div
        className={
          blownUpIdx >= 0
            ? 'relative grid grid-cols-[80px_1fr_80px] grid-rows-[80px_1fr_80px] gap-3'
            : 'grid grid-cols-2 gap-3 sm:grid-cols-4'
        }
      >
        {items.map((item, idx) => {
          const isBlownUp = blownUpIdx === idx;
          const isSibling = blownUpIdx >= 0 && !isBlownUp;
          const isPeeking = peekIdx === idx && isSibling;
          const wrapperClass = isBlownUp
            ? 'col-start-2 col-end-3 row-start-2 row-end-3 flex flex-col gap-1 rounded-xl border-2 border-accent bg-neutral-100 p-2 dark:bg-neutral-800'
            : isSibling
              ? isPeeking
                ? 'flex flex-col gap-1 rounded-xl border border-accent bg-neutral-100 p-1 opacity-90 dark:bg-neutral-800'
                : 'flex flex-col gap-1 rounded-xl border border-neutral-200 bg-neutral-100 p-1 opacity-40 dark:bg-neutral-800'
              : highlightedIdx === idx
                ? 'flex flex-col gap-1 rounded-xl border border-accent bg-neutral-100 p-1 ring-2 ring-accent dark:bg-neutral-800'
                : 'flex flex-col gap-1 rounded-xl border border-neutral-200 bg-neutral-100 p-1 dark:bg-neutral-800';
          // Position the sibling thumbnails into corners when blown up.
          const cornerClass = isSibling
            ? idx === (blownUpIdx + 1) % 4
              ? 'col-start-3 row-start-1'
              : idx === (blownUpIdx + 2) % 4
                ? 'col-start-1 row-start-3'
                : idx === (blownUpIdx + 3) % 4
                  ? 'col-start-3 row-start-3'
                  : 'col-start-1 row-start-1'
            : '';
          return (
            <div
              key={item.id ?? idx}
              role="group"
              aria-label={`Variation ${idx + 1}`}
              data-testid={`variation-tile-${idx + 1}`}
              data-highlighted={highlightedIdx === idx}
              data-blown-up={isBlownUp}
              className={[wrapperClass, cornerClass].filter(Boolean).join(' ')}
              onMouseEnter={() => {
                setHighlightedIdx(idx);
                if (isSibling) setPeekIdx(idx);
              }}
              onMouseLeave={() => {
                if (isSibling) setPeekIdx(-1);
              }}
            >
              <Tooltip
                value={{
                  plain: tileMetadata(item, idx + 1),
                  technical: tileMetadata(item, idx + 1, true),
                }}
              >
                <button
                  ref={(el) => {
                    tileRefs.current[idx] = el;
                  }}
                  type="button"
                  onClick={() => handleTileClick(idx, item)}
                  onFocus={() => setHighlightedIdx(idx)}
                  className={[
                    'group relative w-full overflow-hidden rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                    isBlownUp ? 'aspect-auto h-full' : 'aspect-square',
                  ].join(' ')}
                  aria-label={`Pick variation ${idx + 1}, seed ${item.seed}`}
                >
                  <img
                    src={resolveAssetUrl(item.thumbnail_url || item.image_url)}
                    alt={describeVariation(item, idx + 1)}
                    className="h-full w-full object-cover motion-safe:transition-transform motion-safe:group-hover:scale-[1.02]"
                    loading="lazy"
                  />
                </button>
              </Tooltip>
              <div className="flex items-center justify-between gap-1 px-1 pb-1 text-[10px] uppercase tracking-wide text-neutral-700 dark:text-neutral-300">
                <span className="font-semibold">
                  Var {idx + 1}
                  {isBlownUp ? ' (active)' : ''}
                </span>
                <span className="font-mono text-[9px] text-neutral-500 dark:text-neutral-400">
                  seed {item.seed}
                </span>
              </div>
              {isBlownUp && (
                <div className="flex items-center justify-center gap-2 px-2 pb-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPick(item);
                    }}
                    aria-label={lang === 'plain' ? 'Use this one' : 'Select'}
                    data-testid={`variation-pick-${idx + 1}`}
                    className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md bg-accent px-5 text-sm font-semibold text-neutral-950 hover:bg-accent-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                  >
                    {lang === 'plain' ? 'Use this one' : 'Select'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setBlownUpIdx(-1)}
                    aria-label={lang === 'plain' ? 'Close' : 'Collapse variations'}
                    className="inline-flex h-10 cursor-pointer items-center justify-center rounded-md border border-neutral-300 px-4 text-sm text-neutral-800 hover:bg-neutral-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent dark:border-neutral-700 dark:text-neutral-100 dark:hover:bg-neutral-800"
                  >
                    {lang === 'plain' ? 'Close' : 'Collapse'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* v7.3 — tip strip when blown up. */}
      {blownUpIdx >= 0 && (
        <p className="text-xs text-neutral-600 dark:text-neutral-400">
          {lang === 'plain'
            ? 'Hover the small images to peek at them. Use arrow keys to peek, Enter to pick.'
            : 'Cycle via arrow keys. Enter picks focused candidate. Click outside or press Escape to collapse.'}
        </p>
      )}

      {!blownUpIdx && blownUpIdx === -1 && (
        <p className="text-[10px] text-neutral-500 dark:text-neutral-500">
          {lang === 'plain'
            ? 'Click any tile to expand it. Use arrow keys to peek, Enter to pick.'
            : 'Click a tile to expand. Arrow keys to focus, Enter to pick.'}
        </p>
      )}
    </div>
  );
}

/** Compact 2-3 line summary of one variation for the hover tooltip. */
function tileMetadata(item: ChatResponseItem, idx: number, technical = false): string {
  const p = item.parameters;
  const lines: string[] = [];
  lines.push(`Variation ${idx} — seed ${item.seed}`);
  if (p) {
    const iris = p.iris_color ? (technical ? `iris ${p.iris_color}` : `iris ${hexToColourName(p.iris_color)}`) : null;
    if (iris) lines.push(iris);
    if (p.emotion) lines.push(`mood: ${p.emotion}`);
    if (p.lighting?.quality) lines.push(`lighting: ${p.lighting.quality}`);
  }
  return lines.join('\n');
}

function describeVariation(item: ChatResponseItem, idx: number): string {
  const p = item.parameters;
  const parts: string[] = [`Variation ${idx}`];
  if (p) {
    if (p.iris_color) parts.push(`iris ${p.iris_color}`);
    if (p.iris_pattern && p.iris_pattern !== 'radial') parts.push(`${p.iris_pattern} pattern`);
    if (p.catchlight?.enabled) parts.push('with catchlight');
    else parts.push('no catchlight');
    if (p.eyelid_shape && p.eyelid_shape !== 'open') parts.push(`${p.eyelid_shape}-closed eyelid`);
    if (p.reflected_scene?.enabled && p.reflected_scene.preset) {
      parts.push(`reflecting ${p.reflected_scene.preset}`);
    }
  }
  return parts.join(', ');
}
